import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { BookingPrefill } from '@/components/forms/booking-form'

import type {
    AccommodationCardData,
    AccommodationDetailData,
    AccommodationOption,
    PublicPageProps
} from '@/types'

import Link from 'next/link'
import { ArrowLeft, Bath, BedDouble, MapPin, Ruler, Users, Waves } from 'lucide-react'
import { cms } from '@/lib/cms'
import { formatMoney } from '@/lib/format'
import { fill, useI18n } from '@/i18n'
import { Seo } from '@/components/generals/seo'
import { BookingForm } from '@/components/forms/booking-form'
import { amenityIcon } from '@/components/site/amenity-icon'
import { AccommodationCard } from '@/components/site/accommodation-card'
import { SectionHeading, SiteLayout } from '@/components/site/site-layout'
import { bookingPrefillFor, loadPublicContext, localeOf, routeParam } from '@/lib/server/ssr'
import { getAccommodationBySlug, listAccommodations } from '@/lib/server/accommodations'

interface DetailProps extends PublicPageProps {
    item: AccommodationDetailData
    others: Array<AccommodationCardData>
    option: AccommodationOption
    prefill: BookingPrefill | null
}

const AccommodationDetail: FC<DetailProps> = ({ item, others, option, prefill, ...base }): ReactNode => {
    const { t, locale } = useI18n()
    const hasSharedPool = item.amenities.includes('shared_pool')

    const facts = [
        { icon: Users, label: t.detail.guests, value: item.maxGuests },
        { icon: BedDouble, label: t.detail.bedrooms, value: item.bedrooms },
        { icon: Bath, label: t.detail.bathrooms, value: item.bathrooms },
        { icon: Ruler, label: t.detail.size, value: item.sizeSqm !== null ? fill(t.common.sqm, { count: item.sizeSqm }) : null },
        { icon: Waves, label: t.detail.pool, value: item.hasPool ? t.detail.poolYes : hasSharedPool ? t.amenities.shared_pool : t.detail.poolNo }
    ].filter(fact => fact.value !== null && fact.value !== undefined)

    return (
        <SiteLayout {...base}>
            <Seo title={item.name} description={item.summary?.value ?? item.tagline?.value} ogImage={item.coverImage ?? undefined} />

            <article id='accommodation'>
                <section className='mx-auto max-w-7xl px-4 pt-8 sm:px-6 lg:px-8'>
                    <Link href='/accommodations' className='inline-flex items-center gap-2 text-sm font-semibold text-navy/70 transition-colors hover:text-navy'>
                        <ArrowLeft className='size-4 rtl:rotate-180' aria-hidden='true' />
                        {t.detail.back}
                    </Link>

                    <div className='mt-6 flex flex-wrap items-end justify-between gap-6'>
                        <div className='max-w-3xl'>
                            <p className='flex items-center gap-2 text-xs font-bold tracking-[0.2em] text-gold uppercase'>
                                <MapPin className='size-4' aria-hidden='true' />
                                <span {...cms('accommodations', item.id, 'location')}>{item.location}</span>
                                <span aria-hidden='true'>·</span>
                                <span>{t.types[item.type]}</span>
                            </p>
                            <h1 className='mt-3 text-4xl leading-tight font-semibold text-navy md:text-6xl'>
                                <span {...cms('accommodations', item.id, 'name')}>{item.name}</span>
                            </h1>
                            {item.tagline && (
                                <p className='mt-4 text-lg text-muted-foreground' {...cms('accommodations', item.id, item.tagline.column)}>{item.tagline.value}</p>
                            )}
                        </div>

                        <div className='rounded-2xl bg-sand px-5 py-4 text-end'>
                            {item.pricePerNight !== null
                                ? (
                                    <p className='text-sm text-muted-foreground'>
                                        <span>{t.common.from} </span>
                                        <span className='font-heading text-3xl font-semibold text-navy'>{formatMoney(item.pricePerNight, locale)}</span>
                                        <span> {t.common.perNight}</span>
                                    </p>
                                )
                                : <p className='font-semibold text-navy'>{t.common.onRequest}</p>}
                            <p className='mt-1 text-xs text-muted-foreground'>{t.detail.priceNote}</p>
                        </div>
                    </div>

                    {item.coverImage && (
                        <div className='mt-8 overflow-hidden rounded-[2rem] bg-sand shadow-xl'>
                            {/* eslint-disable-next-line @next/next/no-img-element */}
                            <img
                                src={item.coverImage}
                                alt={item.coverImageAlt ?? item.name}
                                className='aspect-[16/9] max-h-[640px] w-full object-cover md:aspect-[21/9]'
                            />
                        </div>
                    )}
                </section>

                <div className='mx-auto grid max-w-7xl gap-12 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_420px] lg:gap-16 lg:px-8'>
                    <div className='flex flex-col gap-14'>
                        <section id='facts' aria-label={t.detail.aboutTitle}>
                            <dl className='grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-5'>
                                {facts.map(fact => (
                                    <div key={fact.label} className='rounded-2xl border border-border bg-card p-4'>
                                        <fact.icon className='size-5 text-gold' aria-hidden='true' />
                                        <dt className='mt-3 text-xs font-semibold tracking-wide text-muted-foreground uppercase'>{fact.label}</dt>
                                        <dd className='mt-1 text-lg font-bold text-navy'>{fact.value}</dd>
                                    </div>
                                ))}
                            </dl>
                        </section>

                        {(item.summary || item.description) && (
                            <section id='description'>
                                <h2 className='text-3xl font-semibold text-navy'>{t.detail.aboutTitle}</h2>
                                {item.summary && (
                                    <p className='mt-5 text-lg leading-relaxed font-medium text-navy/90' {...cms('accommodations', item.id, item.summary.column)}>
                                        {item.summary.value}
                                    </p>
                                )}
                                {item.description && (
                                    <p className='mt-5 leading-relaxed whitespace-pre-line text-foreground/80' {...cms('accommodations', item.id, item.description.column)}>
                                        {item.description.value}
                                    </p>
                                )}
                            </section>
                        )}

                        {item.amenities.length > 0 && (
                            <section id='amenities'>
                                <h2 className='text-3xl font-semibold text-navy'>{t.detail.amenitiesTitle}</h2>
                                <ul className='mt-6 grid gap-3 sm:grid-cols-2'>
                                    {item.amenities.map((key) => {
                                        const Icon = amenityIcon(key)
                                        const label = (t.amenities as Record<string, string>)[key]

                                        if (!label) return null

                                        return (
                                            <li key={key} className='flex items-center gap-3 rounded-xl bg-sand px-4 py-3 text-sm font-semibold text-navy'>
                                                <Icon className='size-5 shrink-0 text-gold' aria-hidden='true' />
                                                {label}
                                            </li>
                                        )
                                    })}
                                </ul>
                            </section>
                        )}

                        {item.gallery.length > 0 && (
                            <section id='gallery'>
                                <h2 className='text-3xl font-semibold text-navy'>{t.detail.galleryTitle}</h2>
                                <div className='mt-6 grid grid-cols-2 gap-4 md:grid-cols-3'>
                                    {item.gallery.map((src, index) => (
                                        <a key={src} href={src} target='_blank' rel='noopener noreferrer' className='group overflow-hidden rounded-2xl bg-sand'>
                                            {/* eslint-disable-next-line @next/next/no-img-element */}
                                            <img
                                                src={src}
                                                alt={item.galleryAlt[index] || `${item.name} ${index + 1}`}
                                                loading='lazy'
                                                className='aspect-[4/3] w-full object-cover transition-transform duration-500 group-hover:scale-105'
                                            />
                                        </a>
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>

                    <aside id='request' className='lg:sticky lg:top-28 lg:self-start'>
                        <div className='rounded-3xl border border-border bg-card p-6 shadow-[0_24px_60px_-30px_rgba(19,36,58,0.4)] sm:p-7'>
                            <h2 className='text-2xl font-semibold text-navy'>{t.detail.bookingTitle}</h2>
                            <p className='mt-2 text-sm text-muted-foreground'>{t.detail.bookingText}</p>
                            <div className='mt-6'>
                                <BookingForm
                                    accommodations={[option]}
                                    locations={[option.location]}
                                    today={base.today}
                                    prefill={prefill}
                                    lockedAccommodationId={item.id}
                                    compact
                                />
                            </div>
                        </div>
                    </aside>
                </div>

                {others.length > 0 && (
                    <section id='more' className='border-t border-border bg-sand'>
                        <div className='mx-auto max-w-7xl px-4 py-16 sm:px-6 md:py-20 lg:px-8'>
                            <SectionHeading title={t.detail.more} />
                            <div className='mt-10 grid gap-7 md:grid-cols-2 lg:grid-cols-3'>
                                {others.map(other => <AccommodationCard key={other.id} item={other} />)}
                            </div>
                        </div>
                    </section>
                )}
            </article>
        </SiteLayout>
    )
}

export const getServerSideProps: GetServerSideProps<DetailProps> = async (ctx) => {
    const locale = localeOf(ctx)
    const slug = routeParam(ctx.params?.slug)

    const [context, item] = await Promise.all([
        loadPublicContext(ctx),
        getAccommodationBySlug(slug, locale).catch(() => null)
    ])

    if (!item) return { notFound: true }

    const all = await listAccommodations(locale).catch(() => [])
    const others = all.filter(other => other.id !== item.id).slice(0, 3)

    return {
        props: {
            ...context.base,
            item,
            others,
            option: { id: item.id, name: item.name, location: item.location, maxGuests: item.maxGuests, type: item.type },
            prefill: bookingPrefillFor(context.customer)
        }
    }
}

export default AccommodationDetail
